<template>
  <div class="results" ref="results">
    <div class="results-spacing">
      <p class="slate-notification--error" v-if="resultsError">
        Sorry! We had some trouble saving your score or displaying the
        leaderboard. Please try again later.
      </p>
      <br />
      <button
        @click="toggleResultsModal"
        class="slate-button slate-button--juicy"
      >
        See results
      </button>
      <BarModal
        v-if="showResultsModal"
        @close="toggleResultsModal"
        :quizData="quizData"
        :username="username"
        :userLabel="userLabel"
        :score="score"
        :averageScore="averageScore"
        :numCorrect="numCorrect"
        :numQuestions="numQuestions"
      />
    </div>
    <Roadblock v-if="!hasLeaderboardAccess" />
    <div class="results-spacing" v-else>
      <h3>Top Scores</h3>
      <p>
        The top 50 scores on this week's quiz among Slate Plus users and
        Slatesters.
      </p>
      <Leaderboard heading="Slate Plus members" :rows="topSlatePlus" />
      <Leaderboard heading="Slatesters" :rows="topSlatesters" />
    </div>
  </div>
</template>

<script>
import Leaderboard from "./Leaderboard.vue";
import Roadblock from "./Roadblock.vue";
import BarModal from "./BarModal.vue";
import { getCookieValue, setLocalStorageQuiz, pymChild } from "../utils.js";

export default {
  name: "Result",
  components: {
    Leaderboard,
    Roadblock,
    BarModal,
  },

  props: {
    myslateDomain: { type: String, required: true },
    quizId: { type: String, required: true, default: "0" },
    username: { type: String, required: true },
    userLabel: { type: String, required: true },
    hasLeaderboardAccess: { type: Boolean, required: true },
    quizData: { type: Object, required: true },
    numQuestions: { type: Number, required: true },
    numCorrect: { type: Number, required: true },
    score: { type: Number, required: true },
    answers: { type: Array, required: true },
    quizAlreadyCompleted: { type: Boolean, required: true },
    quizCompletedAsNonPlus: { type: Boolean, required: true },
  },
  computed: {
    resultsBody() {
      return JSON.stringify({
        "slate-uuid": getCookieValue("slate-uuid-js"),
        score: this.score,
        answers: this.answers,
      });
    },
  },
  data() {
    return {
      averageScore: 0,
      topSlatePlus: [],
      topSlatesters: [],
      highestScore: -1,
      resultsError: false,
      showResultsModal: false,
    };
  },
  methods: {
    toggleResultsModal() {
      this.showResultsModal = !this.showResultsModal;
    },
    setLeaderboardData(boardData) {
      const convertUser = (user) => ({ ...user, username: user.display_name });
      this.topSlatePlus = boardData.members.map(convertUser);
      this.topSlatesters = boardData.staff.map(convertUser);
      this.averageScore = Math.round(boardData.average_score);
      this.showResults = true;
    },
    fetchLeaderBoardData() {
      const options = {
        method: "GET",
        credentials: "include", //pass cookies
        headers: {
          Accept: "application/json",
        },
      };
      return fetch(
        `${this.myslateDomain}/quizzes/leaderboard/${this.quizId}/`,
        options
      )
        .then((response) => response.json())
        .then((response) => {
          this.setLeaderboardData(response);
        })
        .catch((error) => {
          console.warn("Error fetching leaderboard:", error);
          this.resultsError = true;
        });
    },
    postScoreData() {
      const options = {
        method: "POST",
        credentials: "include", //pass cookies
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json; charset=UTF-8",
        },
        body: this.resultsBody,
      };

      return fetch(
        `${this.myslateDomain}/quizzes/score/${this.quizId}/`,
        options
      )
        .then((response) => response.json())
        .then((response) => {
          this.setLeaderboardData(response);
        })
        .catch((error) => {
          console.warn("Error posting user score:", error);
          this.resultsError = true;
        });
    },
  },
  async mounted() {
    if (this.quizAlreadyCompleted) {
      if (this.quizCompletedAsNonPlus && this.hasLeaderboardAccess) {
        // Quiz was completed previously as non SPlus or Quiz sub, user is now logged in as SPlus or Quiz sub then score will be saved
        // presumably they just logged in and redirected back here, so jump immediately to the results
        await this.postScoreData();
        // this.scrollToResults({ behavior: "auto" });
      } else {
        // Quiz was completed previously but there's no need to save because:
        // The quiz was already saved as SPlus user, or the quiz was already saved locally as non Splus user and they haven't subscribed
        await this.fetchLeaderBoardData();
      }
      this.showResultsModal = true;
    } else {
      const amplitudeData = {
        eventName: "Quiz - Results",
        properties: {
          "Completed Question": this.numQuestions,
        },
      };

      pymChild.sendMessage("tracking", JSON.stringify(amplitudeData));

      if (this.hasLeaderboardAccess) {
        // Quiz was just completed, the user is SPlus then score will be saved
        await this.postScoreData();
      } else {
        // Quiz was just completed, the user is non Splus then score will be saved to local storage
        setLocalStorageQuiz(this.quizId, this.resultsBody);
        await this.fetchLeaderBoardData();
      }
    }
  },
};
</script>

<style scoped lang="scss">
.results {
  display: flow-root;
  position: relative;
}

.results-spacing {
  padding: 0 1rem;
}
</style>
